/**
 * Team Logo Utility
 *
 * Generates team logo URLs for the configured Azure Blob Storage account.
 * Mirrors the backend's DraftKingsTeamLogoProvider logic.
 *
 * Storage Structure:
 * - Account: configured via VITE_BLOB_STORAGE_ACCOUNT_NAME (no default — see below)
 * - Container: configured via VITE_BLOB_STORAGE_CONTAINER (default: picks-core)
 *
 * Path Pattern (all sports use sport-specific paths):
 * - {container}/{sport}/teams/{teamShortcode}.{ext}
 *
 * Examples (account `operatorassets`):
 * - NFL: https://operatorassets.blob.core.windows.net/picks-core/nfl/teams/KC.png
 * - NBA: https://operatorassets.blob.core.windows.net/picks-core/nba/teams/LAL.png
 * - NCAAMB: https://operatorassets.blob.core.windows.net/picks-core/ncaamb/teams/DUKE.png
 *
 * With no account configured this returns undefined (CAE-676): there is
 * deliberately no default account, since a URL built against one would point at
 * another operator's estate.
 */

import { getFileExtForSport } from '@low6-pickem/sports-config';
import { getBlobAssetBaseUrl } from './blobStorage';

/** Session-level cache buster — ensures replaced images are never stale */
const SESSION_CACHE_BUSTER = Date.now();

export interface TeamLogoParams {
  sport: string;
  teamShortcode: string;
}

/**
 * Generate team logo URL for DraftKings Azure Storage
 *
 * @param params - Sport and team shortcode
 * @returns Full URL to the team logo image, or undefined if params are invalid
 */
export function getTeamLogoUrl(params: TeamLogoParams): string | undefined {
  const { sport, teamShortcode } = params;

  // Validate required parameters
  if (!sport || !teamShortcode) {
    return undefined;
  }

  const baseUrl = getBlobAssetBaseUrl();
  if (!baseUrl) return undefined;

  // Normalize inputs
  const normalizedSport = sport.toLowerCase();
  const normalizedShortcode = teamShortcode.toUpperCase();

  // NCAA Football (DKPLAT-559): the "team logo" is non-IP HELMET artwork (PNG)
  // served from a dedicated /helmet path, not the standard /teams/{code}.svg.
  // Keep in sync with jersey-mapping.ts (generateTeamLogoUrl) and the backend's
  // DraftKingsTeamLogoProvider.
  if (normalizedSport === 'ncaafb') {
    return `${baseUrl}/ncaafb/helmet/${normalizedShortcode}.png?v=${SESSION_CACHE_BUSTER}`;
  }

  // Build URL path - all sports use sport-specific paths: {container}/{sport}/teams/{shortcode}.ext
  // File extensions vary by sport: NBA and NCAAMB use .png, most others use .svg
  const fileExt = getFileExtForSport(normalizedSport);

  return `${baseUrl}/${normalizedSport}/teams/${normalizedShortcode}.${fileExt}?v=${SESSION_CACHE_BUSTER}`;
}
